/**
 * Wire-format schemas: parse JSON exactly as the server sends it, then
 * transform to our domain types (camelCase, derived fields).
 *
 * Schemas live close to the API client; entity types live in src/entities.
 */
import { z } from 'zod'

import { SiteSchema, type Site } from '@/entities/site'
import { UserSchema, type User } from '@/entities/user'

const UserWireSchema = z
  .object({
    id: z.number().int().positive(),
    email: z.string().email(),
    display_name: z.string(),
    locale: z.string().optional(),
  })
  .transform(
    (u): User => ({
      id: u.id,
      email: u.email,
      displayName: u.display_name,
      ...(u.locale !== undefined && { locale: u.locale }),
    }),
  )

/**
 * Per-site feature flags as `/auth/me` ships them. Optional everywhere
 * — older builds or sites without the plugin just leave the slot
 * missing. For richer info (config, endpoints, status reasons), use
 * the per-site capabilities endpoint (`ezqr/v1/site`) instead — see
 * memory/site-capabilities-contract.md.
 */
const SiteFeaturesWireSchema = z
  .object({
    aiUploader: z.object({ active: z.boolean() }).partial().optional(),
  })
  .partial()
  .optional()

const SiteWireSchema = z
  .object({
    id: z.number().int().positive(),
    domain: z.string(),
    path: z.string(),
    name: z.string(),
    roles: z.array(z.string()),
    features: SiteFeaturesWireSchema,
  })
  .transform(
    (s): Site => ({
      id: s.id,
      domain: s.domain,
      path: s.path,
      name: s.name,
      roles: s.roles,
      ...(s.features !== undefined && { features: s.features }),
    }),
  )

export const userWire = UserWireSchema
export const siteWire = SiteWireSchema

// Re-export for convenience
export { UserSchema, SiteSchema }
export type { User, Site }
